import { MouseEvent, ReactNode } from "react";
import { Icon } from "@chakra-ui/react";
import { MdArrowForward } from "react-icons/md";
import { useRouter } from "next/router";
import LinkButton, { LinkButtonProps } from "components/Shared/LinkButton";
import { useSetCurrentBrandId } from "utils/currentUserHooks";
import { assertBrandId } from "utils/assertions";
import asyncVoidHandler from "utils/asyncVoidHandler";
import { BRAND_INDEX, Url } from "utils/links";

type UseOpenBrandAccountOptions = {
  brandId: UUID | null | undefined;
  href?: Url;
  onClick?: (evt: MouseEvent<HTMLButtonElement & HTMLAnchorElement>) => void;
};

export function useOpenBrandAccount({
  brandId,
  href = BRAND_INDEX,
  onClick,
}: UseOpenBrandAccountOptions) {
  const router = useRouter();

  const setCurrentBrandId = useSetCurrentBrandId();

  return async function handleClick(
    evt: MouseEvent<HTMLButtonElement & HTMLAnchorElement>,
  ) {
    evt.preventDefault();
    assertBrandId(brandId);
    await setCurrentBrandId(brandId, true);
    onClick?.(evt);
    await router.push(href);
  };
}

type Props = LinkButtonProps & {
  brandId: UUID | null | undefined;
  children?: ReactNode;
};

export default function OpenBrandAccountButton({
  brandId,
  children = "Open Brand Account",
  isDisabled,
  href = BRAND_INDEX,
  onClick,
  ...rest
}: Props) {
  const handleClick = useOpenBrandAccount({ brandId, href, onClick });

  return (
    <LinkButton
      href={brandId ? (href as string) : undefined}
      colorScheme="pinkButton"
      size="sm"
      px="1.25em"
      w="100%"
      rightIcon={<Icon as={MdArrowForward} />}
      isDisabled={!brandId || isDisabled}
      onClick={asyncVoidHandler(handleClick)}
      {...rest}
    >
      {children}
    </LinkButton>
  );
}
